import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { FiMoreVertical, FiSearch } from "react-icons/fi";
import { FaTrash, FaShareAlt } from "react-icons/fa";
import axios from "axios";
import Sidebar from "./Sidebar";
import Header from "./Header";
import Footer from "./Footer";
import NavButtons from "./NavButtons";
import emptystate from "../image/emptystate.png";
import Loader from "./Loader";
import SwipeContainer from "../utils/SwipeContainer";
import {
  ensureValidToken,
  isOfflineMode,
  OFFLINE_CACHE_KEYS,
  cacheData,
  getCachedData,
} from "../utils/authService";
import "../styles/Homemycards.css";

const Homemyholder = () => {
  const [menuOpen, setMenuOpen] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("my-holder");
  const [holderItems, setHolderItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [removing, setRemoving] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [offline, setOffline] = useState(isOfflineMode());
  const menuRef = useRef(null);
  const navigate = useNavigate();

  const fetchSavedCards = async () => {
    // Offline mode: use cached data only, no API calls
    if (isOfflineMode()) {
      const cached = getCachedData(OFFLINE_CACHE_KEYS.holder, []);
      setHolderItems(cached);
      setFilteredItems(cached);
      setOffline(true);
      setLoading(false);
      return;
    }

    try {
      const accessToken = await ensureValidToken();
      if (!accessToken) {
        navigate("/login");
        return;
      }
      const response = await axios.get("https://api.nexcard.co.in/api/cards/my-holder/", {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      const results = response.data.results || [];
      setHolderItems(results);
      setFilteredItems(results);
      cacheData(OFFLINE_CACHE_KEYS.holder, results);
      setOffline(false);
    } catch (error) {
      console.error("Error fetching saved cards:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSavedCards();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (searchQuery.trim() === "") {
      setFilteredItems(holderItems);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = holderItems.filter(item => {
        const card = item.card;
        return (
          card.full_name?.toLowerCase().includes(query) ||
          card.company_name?.toLowerCase().includes(query) ||
          card.designation?.toLowerCase().includes(query)
        );
      });
      setFilteredItems(filtered);
    }
  }, [searchQuery, holderItems]);

  const handleRemoveFromHolder = async (holderId, e) => {
    e.stopPropagation();

    if (isOfflineMode()) {
      alert("You are offline. Removing cards from My Holder is disabled. Showing last saved data.");
      return;
    }

    setRemoving(true);
    
    try {
      const accessToken = await ensureValidToken();
      if (!accessToken) {
        navigate("/login");
        return;
      }
      await axios.delete(`https://api.nexcard.co.in/api/cards/my-holder/${holderId}/`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      fetchSavedCards();
    } catch (error) {
      console.error("Error removing card:", error);
      alert("Failed to remove card. Please try again.");
    } finally {
      setRemoving(false);
    }
  };

  const handleCardClick = (cardId) => {
    navigate(`/card-details/${cardId}`);
  };

  const handleShare = (cardId, e) => {
    e.stopPropagation();
    
    // Find the card to get the owner's name
    const holderItem = holderItems.find(item => item.card.id === cardId);
    const ownerName = holderItem?.card?.full_name || 'Someone';
    
    const shareUrl = new URL(`${window.location.origin}/shared-card/${cardId}`);
    shareUrl.searchParams.set('from', 'holder');
    
    const shareText = `Hey! 👋\nHere's the digital business card of *${ownerName}* —\n🔗 Click the link to Open:`;
    
    if (navigator.share) {
      navigator.share({
        title: `${ownerName}'s Business Card`,
        text: shareText,
        url: shareUrl.toString()
      });
    } else {
      // For desktop and fallback, include URL in the clipboard text
      const clipboardText = `${shareText} ${shareUrl.toString()}`;
      navigator.clipboard.writeText(clipboardText);
      alert("Message with link copied to clipboard!");
    }
  };

  const toggleMenu = (index) => {
    setMenuOpen(menuOpen === index ? null : index);
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (loading) {
    return <Loader />;
  }

  return (
    <>
      <Sidebar isOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />
      <Header toggleSidebar={toggleSidebar} />
      <NavButtons activeTab={activeTab} setActiveTab={setActiveTab} />

      <SwipeContainer>
        {activeTab === "my-holder" && (
          <div className="main-content">
            {offline && (
              <div className="offline-banner">
                You are in offline mode. Showing last saved holder cards.
              </div>
            )}
            <div className="search-container">
              <div className="search-bar">
                <FiSearch className="search-icon" />
                <input 
                  type="text" 
                  placeholder="Search Name/ Company/ Designation..." 
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>

            {filteredItems.length === 0 ? (
              <div className="empty-state-container">
                <img
                  src={emptystate}
                  alt="Empty Space"
                  className="empty-image"
                />
                <p className="empty-text">
                  {searchQuery 
                    ? "No matching cards found in your holder" 
                    : "Your space is empty! Add and organize your Cards collection"
                  }
                </p>
              </div>
            ) : (
              <div className="cards-list">
                {filteredItems.map((item, index) => {
                  const card = item.card;
                  return (
                    <div 
                      key={item.id} 
                      className="card" 
                      onClick={() => handleCardClick(card.id)}
                    >
                      <div className="card-header">
                        <div className="profile">
                          {card.profile_image ? (
                            <img 
                              src={card.profile_image} 
                              alt="Profile" 
                              onError={(e) => {
                                e.target.src = "https://www.pngitem.com/pimgs/m/78-786293_1240-x-1240-0-avatar-profile-icon-png.png";
                              }}
                            />
                          ) : (
                            <img
                              src="https://www.pngitem.com/pimgs/m/78-786293_1240-x-1240-0-avatar-profile-icon-png.png"
                              alt="Default Profile"
                            />
                          )}
                        </div>
                        <button
                          className="menu-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleMenu(index);
                          }}
                        >
                          <FiMoreVertical size={20} />
                        </button>
                      </div>

                      <div className="card-image-container">
                        {card.file?.file.endsWith('.pdf') ? (
                          <div className="pdf-preview">
                            <div className="pdf-icon">PDF</div>
                            <span>{card.file?.name || 'Document'}</span>
                          </div>
                        ) : (
                          <img src={card.file?.file} 
                          alt="Business Card"
                          className="card-image" />
                        )}
                      </div>

                      {menuOpen === index && (
                        <div className="dropdown-menu" ref={menuRef} onClick={(e) => e.stopPropagation()}>
                          <button
                            className="menu-item"
                            onClick={(e) => handleShare(card.id, e)}
                          >
                            <FaShareAlt /> Share
                          </button>
                          <button
                            className="menu-item"
                            onClick={(e) => handleRemoveFromHolder(item.id, e)}
                            disabled={removing}
                          >
                            <FaTrash /> {removing ? "Removing..." : "Remove from My Holder"}
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            <Footer />
          </div>
        )}
      </SwipeContainer>
    </>
  );
};

export default Homemyholder;